import React, { useEffect } from 'react';
import _ from 'lodash';
import pivot from 'data-pivot';
import md5 from 'md5';
import { Alert } from 'reactstrap';

import { modifyHash, noVal, space, stopEvent } from '../base/utils/miscutils';
import C from '../C';
import printer from '../base/utils/printer';
import ServerIO from '../plumbing/ServerIO';
import DataStore from '../base/plumbing/DataStore';
import ChartWidget from '../base/components/ChartWidget';
import Misc from '../base/components/Misc';
import PropControl from '../base/components/PropControl';
import {Card} from '../base/components/CardAccordion';
import SearchQuery from '../base/searchquery';
import SimpleTable, {CellFormat} from '../base/components/SimpleTable';
import { getDataLogData, pivotDataLogData } from '../base/plumbing/DataLog';
import { assMatch } from '../base/utils/assert';

window.pivot = pivot; // for debug


const tempFiltersPath = ['widget', 'trafficReport', 'tempFilters'];

const getUrlFilters = () => ({
	q: DataStore.getUrlValue('q'),
	start: DataStore.getUrlValue('start'),
	end: DataStore.getUrlValue('end'),
	bby: DataStore.getUrlValue('bby'),
	bby2: DataStore.getUrlValue('bby2'),
});

const isFiltersModified = () => {
	// read unedited valued off the url
	const urlFilters = getUrlFilters();
	const tempFilters = DataStore.getValue(tempFiltersPath) || {};

	let modified = Object.keys(urlFilters).find(k => {
		// "==" operator allows "" == undefined
		/* eslint-disable-next-line eqeqeq */
		if (urlFilters[k] == tempFilters[k]) return false;
		if (!urlFilters[k] && !tempFilters[k]) return false;
		return true;
	});
	return modified;
};

/**
 * Set e.g. publisher:thesun as a query filter
 * @param {String} param
 * @param {*} key
 */
const addFilter = (param, key, returnOnly) => {
	// get the query
	let q = DataStore.getUrlValue('q') || '';
	// modify it
	let sq = new SearchQuery(q);
	let sq2 = SearchQuery.setProp(sq, param, key);
	q = sq2.query;

	// Change the window URL, unless specifically asked not to
	if (!returnOnly) {
		DataStore.setUrlValue('q', q);
	}
	
	return modifyHash(null, {q}, returnOnly);
};

/**
 * nice looking numbers
 * @param {*} v
 */
const displayValue = (v) => {
	if (_.isNumber(v)) {
		// 1 decimal place
		v = Math.round(v*10)/10;
		// commas
		v = printer.prettyNumber(v, 10);
	}
	return v;
};

const typeFromParam = param => {
	return {vert: C.TYPES.Advert, pub: C.TYPES.Publisher, cid:C.TYPES.Charity}[param];
};


/**
 * Add a link that modifies the search query in the url to set key:val
 * NB: This is q=foo specific
 */
function LinkMod({param, value, text = value}) {
	assMatch(param, String);

	// Can't modify query for null value, but allow setting param to ""
	if (noVal(value)) return <span>{text}</span>;

	const href = addFilter(param, value, true);
	const onClick = e => { stopEvent(e); addFilter(param, value); };

	return <a href={href} onClick={onClick}>{text}</a>;
}


/**
 * All the base data-fetching code for the TrafficReport component
 * Pulled out of TrafficReport so that:
 * - we can do the massive pivots once per fetch instead of once per draw
 * - we can put the results in DataStore & maintain identity for memoize purposes
 */
const getMergedData = ({q, start, end, bby2, byHostOrAd}) => {
	assMatch(byHostOrAd, String);

	// paths for storing data are unqiue to the filtering
	let dspec = md5(JSON.stringify({q, start, end, byHostOrAd}));

	const mpath = ['widget', 'TrafficReport', 'dt', dspec, bby2];
	const processedPath = ['widget', 'TrafficReport', 'processed', dspec];

	// Where does ad activity data go??
	// dataspaces: trk for evt.pxl, goodloop for evt.viewable, adview, click, close, open, opened
	// see https://github.com/good-loop/doc/wiki/Canonical-Terminology-for-Logging-Good-Loop-Events
	const breakdowns = ['evt/time', 'evt', 'evt/' + byHostOrAd, byHostOrAd];	
	const pgldata = getDataLogData({q, breakdowns, start, end, name:"all-events"});	
	const gldata = pgldata.value;

	// page impressions
	const ptrkdata = getDataLogData({q, start, end, breakdowns: [byHostOrAd],dataspace:'trk',name:'trk-impressions'});
	const trkdata = ptrkdata.value;

	// view length
	const pdtdata = getDataLogData({
		q: space(q, "evt:close"), 
		start, end, 
		breakdowns: [byHostOrAd + '{"dt":"avg"}'], // TODO backend support
		dataspace: ServerIO.DATALOG_DATASPACE,
		name:'view-length'
	});
	const dtdata = pdtdata.value;

	// matrix data?
	const pvMatrixData = bby2? 
		getDataLogData({name:'matrix', q, start, end, breakdowns: [byHostOrAd+'/'+bby2], dataspace:ServerIO.DATALOG_DATASPACE})
		: {};
	const matrixData = pvMatrixData.value;

	// The place we put all the results
	const processedData = DataStore.getValue(processedPath) || {};

	let { evthostdata = {}} = processedData; // pull out for brevity

	let update = false; // Save results back to DataStore only if changed

	// The table data
	if (gldata && ! processedData.hasGlData) {
		let evthostcount = pivotDataLogData(gldata, ["evt", byHostOrAd]);
		evthostdata = { // new object to break identity for memo
			...evthostdata, // existing evthostdata may already have .pxl or .average_view_seconds
			...evthostcount
		};
		processedData.hasGlData = true;
		update = true;
	}

	// evtdata2 hasn't been generated yet
	if (gldata && !processedData.evtdata2) {
		// pivot the data from ES output to chart.js format
		let evtdata = pivot(gldata, 'by_evt_time.buckets.$bi.{key, by_time.buckets.$bi2.$bucket}', '$key.$bi2.$bucket');
		processedData.evtdata2 = pivot(evtdata, '$key.$bi2.{key_as_string, count}', '$key.$key_as_string.$count');
		update = true; // merged is updated, save results
	}

	// Add in trk by host/vert
	if (trkdata && ! processedData.hasTrkData) {
		let trkByHost = pivotDataLogData(trkdata, [byHostOrAd]);
		evthostdata = { // new object to break identity for memo
			...evthostdata,
			pxl: trkByHost
		};
		processedData.hasTrkData = true;
		update = true;
	}

	// add in viewing time by host/vert
	if (dtdata && !processedData.hasDtData) {
		let dtByHost0 = pivot(dtdata, `by_${byHostOrAd}.buckets.$bi.{key, dt}`, '$key.$dt');
		let dtByHost = pivot(dtByHost0, '$key.avg.$avg', '$key.$avg');
		// from millisecs to seconds
		Object.keys(dtByHost).forEach(key => dtByHost[key] /= 1000);
		evthostdata = { // new object to break identity for memo
			...evthostdata,
			average_view_seconds: dtByHost,
		};
		processedData.hasDtData = true;
		update = true;
	}

	// add in matrix data
	if (matrixData && !processedData.matrixData) {
		processedData.matrixData = matrixData;
		update = true;
	}

	// evthostdata may have been assigned with a copy instead of mutated so make sure it's put back into mergedData
	processedData.evthostdata = evthostdata;

	// and save results if changed
	if (update) DataStore.setValue(processedPath, processedData, false);

	return processedData;
};


function TrafficReport() {
	// HACK - allow access to e.g. sogive data
	const d = DataStore.getUrlValue('d');
	if (d) ServerIO.DATALOG_DATASPACE = d;

	const fmod = isFiltersModified();

	const {q, start, end, bby, bby2} = getUrlFilters();
	let byHostOrAd = bby || 'pub';

	// evtdata2 = {event: {date: count}}
	const { evthostdata, evtdata2, matrixData } = getMergedData({q, start, end, byHostOrAd, bby2});	
	if (evtdata2) {
		console.log("evtdata2", evtdata2);
	}
	// no data
	if (!evthostdata) {
		return (
			<div className="TrafficReport">
				<h3>Fetching your data...</h3>
				<Misc.Loading />
			</div>
		);
	}

	// let tdata = pivot(gldata, "by_tag.buckets.$bi.{key, by_time.buckets.$bi2.$bucket}", "$key.$bi2.$bucket");
	// let tdata2 = pivot(tdata, "$key.$bi2.{key_as_string, myCount.avg.$avg}", '$key.$key_as_string.$avg');

	// TODO if filters modified -> do not show any data
	// display...
	// NB: chart is off by default as it slows the page down
	return (
		<div>
			<h1>Traffic Report</h1>
			<p>One month of data, in per-hour segments. Near realtime: Does NOT include the most recent 15 minutes.</p>
			
			<FiltersWidget />

			{fmod ? <Alert>Data below is for OLD filter settings</Alert> : null}

			<Misc.CardAccordion widgetName="TrafficReport" multiple defaultOpen>
				<Card title={'Activity Matrix by ' + byHostOrAd} className="full-width">
					<EventHostTable param={byHostOrAd} data={evthostdata} />
				</Card>

				<Card title="Events over Time">
					{evtdata2 ? <ChartWidget dataFromLabel={evtdata2} /> : <Misc.Loading />}
				</Card>

				<Card title="Clicks over Time">
					{evtdata2? <ClicksOverTime count4day={evtdata2.click} /> : <Misc.Loading />}
				</Card>

				<Card title={`Matrix: ${byHostOrAd} x ${bby2 || '?'}`}>
					<MatrixPlot rowDim={byHostOrAd} colDim={bby2} data={matrixData} />
				</Card>

				{/* <Card title="Donations">
					<DonationsCard start={start} end={end} q={q} />
				</Card>

				<Card title="Money">
					<MoneyCard start={start} end={end} q={q} byHostOrAd={byHostOrAd} />
				</Card> */}

				{/* <Card title="Download / Export">
					<a href="/activity.csv" target="_blank">Export of minview (completed video view) events</a>.
					This is for Amy's spreadsheets.
				</Card> */}
			</Misc.CardAccordion>
		</div>
	);
} // ./TrafficReport


function ClicksOverTime({count4day}) {	
	if ( ! count4day) {
		return <>No click data?</>;
	}
	let data = Object.keys(count4day).map(day => { return {date:day.substring(0,10), clicks:count4day[day]}; });
	return <SimpleTable data={data} hasCsv columns={["date", "clicks"]} />;
}

// Memoized to try and speed up dashboard redraws
const MatrixPlot = React.memo(({rowDim, colDim, data}) => {
	let path = ['location', 'params'];
	if (!data) {
		return <>
			<PCSelectBreakdown path={path} prop="bby" />
			<PCSelectBreakdown path={path} prop="bby2" />
		</>;
	}

	// TODO pivot by_A_B.buckets.i.{key:A, by_B:buckets.j.{key,doc_count}}
	let evthostdata0 = pivot(data, `by_${rowDim}_${colDim}.buckets.$bi.{key, by_${colDim}.buckets.$bi2.$bucket}`, '$key.$bi2.$bucket');
	let evthostdata = pivot(evthostdata0, '$evtkey.$bi2.{key, doc_count}', '$evtkey.$key.$doc_count');

	// console.log('MatrixPlot', data);
	// console.log('MatrixPlot pivot', evthostdata);
	let columns = _.uniq(_.flatten(Object.values(evthostdata).map(bucket => Object.keys(bucket))));
	columns = [rowDim].concat(columns);
	columns = columns.slice(0, 1000); // cap the columns - note that hideEmpty will also do that, so cap high

	let tableData = Object.keys(evthostdata).map(k => {
		let kobj = evthostdata[k];
		if (kobj) kobj[rowDim] = k; // paranoid `if`
		return kobj;
	});
	// console.log('MatrixPlot tableData', tableData);

	return <>
		<PCSelectBreakdown path={path} prop="bby" />
		<PCSelectBreakdown path={path} prop="bby2" />
		<SimpleTable data={tableData} columns={columns} hasCsv rowsPerPage={20} hideEmpty />
	</>;
});


function MoneyCard({q, start, end, byHostOrAd}) {
	const spec = `${q} ${start} ${end} ${byHostOrAd}`;
	const donationspath = ['widget', 'TrafficReport', 'donations', spec];

	// donations -- sum the dntn (usually 50% of price)
	let pvDonationsData = DataStore.fetch(donationspath, () => {
		return ServerIO.getSpendData({breakdown:byHostOrAd, q, start, end});
	});

	if (!pvDonationsData.resolved) return <Misc.Loading />;
	if (!pvDonationsData.value) return <p>{pvDonationsData.error}</p>;

	const donationsdata = pvDonationsData.value;
	const donationsdataByCharity = donationsdata['by_' + byHostOrAd];

	return (
		<BreakdownWidget
			data={donationsdataByCharity}
			param={byHostOrAd}
			total={donationsdata.total}
			stats={donationsdata.stats}
		/>
	);
}


function DonationsCard({q, start, end}) {
	const spec = `${q} ${start} ${end}`;
	const donationspath = ['widget', 'TrafficReport', 'donations', spec];

	// donations -- sum the dntn (usually 50% of price)
	let pvDonationsData = DataStore.fetch(donationspath, () => {
		return ServerIO.getDonationsData({q, start, end});
	});

	if (!pvDonationsData.resolved) return <Misc.Loading />;
	if (!pvDonationsData.value) return <p>{pvDonationsData.error}</p>;

	const donationsdata = pvDonationsData.value;
	const donationsdataByCharity = donationsdata.by_cid;

	return (
		<BreakdownWidget
			data={donationsdataByCharity}
			param="cid"
			total={donationsdata.total}
			stats={donationsdata.stats}
		/>
	);
} // ./DonationsCard


/**
 * @param data {evtkey -> key eg host -> doc_count}
 * Memoized to speed up dashboard redraws
 */
const EventHostTable = React.memo(({param, data}) => {
	// This table can be slow in coming - let the user know if not all the data is there yet.
	const noPxl = !data.pxl; // tracking pixel counts
	const noViewSecs = !data.average_view_seconds; // view duration data
	const noRest = !data.req; // proxy for all the other columns
	let incompleteWarning = null;
	if (noPxl || noViewSecs || noRest) {
		incompleteWarning = 'Still waiting for: ';
		const warnings = [
			noRest ? 'main columns' : null,
			noPxl ? 'tracking pixel counts' : null,
			noViewSecs ? 'view durations' : null
		].filter(a => !!a).join(', ');
		incompleteWarning = <Misc.Loading inline text={`Still waiting for: ${warnings}`} />;
	}

	assMatch(param, String);
	// let events = Object.keys(data);
	// TODO £ total, profit-per-impression
	let adevents = [
		'pxl',
		'req', 'reqfail', 'error', 'warning', 'render', 'visible',
		'mouseover',
		'clicktolandingpage',
		'req2', 'reqfail2', 'render2', 'visible2', // landing page stats
		// 'open', 
		'pick',
		'autoplayfail',
		'clicktoplay', 'autoplay', 'tryexpand', 'sfexpanded',
		'minview', 'donation', 'click', 'clickother',
		'startvideo', 'first-quartile', 'midpoint', 'third-quartile', 'endvideo', 'elapseendcard', 'close', 'install',
		'consent-requested', 'consent-yes', 'consent-no',
		'spend', 'spendadjust',
		'average_view_seconds', 'adok', 'adblock'
	];
	
	// HACK extra columns?
	let xcols = DataStore.getUrlValue("xcols");
	if (xcols) {
		let _xcols = xcols.split(",");
		adevents = _xcols.concat(adevents);
	}

	let tooltipForEvent = {
		pxl: "trk:pxl A user has visited a site on which the G-L pixel lives, and now the pixel allows G-L to track if that user interacts with the ad-unit, re-visits the site(s), and/or visits the Advertiser's click-through page. NB: logged in the dataspace `trk`, not the usual `gl` one.",
		req: "The adunit is requested by a call to unit.js. This can be confirmed against our nginx logs. Unlike other events, this event is recorded by the adserver backend calling lg.good-loop.com (so IP and user-agent may be different).",
		reqfail: "A request was made to the server, but failed at that stage.",
		error: "An error occurred when viewing the ad. Dev-Ops can see an example in the json for details. Some errors are handled gracefully.",
		warning: "Like error, but less bad.",
		render: "(render) The advert has displayed itself within the page. This does not mean it is viewable.",
		visible: "(visible) The adunit is viewable (it's 50% on screen for at least a second). Because of the 1 second delay, it is possible for ads to complete via click-through before this event fires.",
		mouseover: "The user has moved their mouse over the ad -- typically a hover indicating attention.",
		clicktolandingpage: "Only used on the click-through-to-our-landing-page-video-player variant. Click through to our video-player landing-page.",
		req2: "With click-through-to-video-landing-page, this is the second request to the server (the one for our video-landing-page).",
		reqfail2: "The req2 request failed.",
		render2: "The render for our video-landing-page. Typically the sequence here is: req -> render -> clicktolandingpage -> req2 -> render2.",
		visible2: "Visible for our video-landing-page.",
		open: "The video play is opened (e.g. the user clicked to open the lightbox), and hopefully the ad should start playing. An example sequence of events might be: open -> autoplayfail -> clicktoplay -> startvideo.",
		autoplayfail: "...We tried to auto-play the video. It didn't play.",
		clicktoplay: "The user clicked on a manual play button (e.g. after an autoplayfail).",
		autoplay: "Auto-play worked :)",
		startvideo: "The video starts playing.",
		'first-quartile': "Video 25% viewed",
		midpoint: "Video 50% viewed",
		'third-quartile': "Video 75% viewed",
		endvideo: "Video 100% viewed",
		tryexpand: "A SafeFrame wrapped adunit tries to expand to show the lightbox.",
		sfexpanded: "A SafeFrame wrapped adunit successfully expanded :) Failures are logged under `error`.",
		pick: "The user has picked a charity.",
		minview: "The user has watched long enough to count as a completed view - the advertiser is charged and a donation will be made to charity.",
		donation: "A donation event. Called when the user has picked a charity provided the video view is complete (i.e. after 14 seconds), or the user clicks-through to the advertiser. This table shows the number of events, and NOT the £ amount.",
		click: "A click through to the advertiser. See logClick in the unit code.",
		clickother: "A click through but not too the advertiser (e.g. to the find-out-more link).",
		close: "The lightbox was closed OR the browser page/tab was closed.",
		install: "To be logged (by the advertiser) if the user installs an app from the advert.",
		'consent-requested': "The user was asked about profile tracking.",
		'consent-yes': "The user said yes to profile tracking.",
		'consent-no': "The user said no to profile tracking.",
		spend: "A budget spend event (e.g. a minview or click). This table shows the number of events, and NOT the £ amount.",
		spendadjust: "A pseudo spend event, made daily to allocate orphan spends from an advert to a charity",
		average_view_seconds: "Use with caution!",
		adok: "There is no adblocker present.",
		adblock: "There is an adblocker present (or rather: an ad-like request failed; this can also be caused by a bad internet connection)."
	};
	// TODO help info for users

	// Custom labels for events
	const headerForEvent = {
		render: 'Impression',
		visible: 'Visible impression (MRC)',
		endvideo: '100% complete'
	};

	// use SimpleTable
	let dataByRow = pivot(data, "$evtkey.$key.$n", "$key.$evtkey.$n");
	let totalRow = dataByRow.total || {};
	totalRow.param = 'total';
	let dataRows = Object.keys(dataByRow).map(k => {
		if (k === 'total') return null;
		return {param: k, ...dataByRow[k]};
	});
	let columns = [
		{
			Header: typeFromParam(param) || param,
			accessor: 'param',
			Cell: v => {
				if (v === 'total') return <em>Total</em>; // NB: th inside td mildly upsets react/browser
				return <LinkMod param={param} value={v} />;
			}
		},
		{
			Header: "req > visible",
			// note re landing page: req2 is only logged when bid is pre-set i.e. a click-through unit.
			// For e.g. Instagram which goes straight to landing page: this has no render or visible event,
			// but that's OK
			accessor: d => d.visible / d.req,
			format: CellFormat.percent
		},
		{
			Header: "visible > minview",
			accessor: d => d.minview / d.visible,
			format: CellFormat.percent
		},
		{
			Header: "Overall (Request > minview)",
			accessor: d => d.minview / d.req,
			format: CellFormat.percent
		}		
	];
	adevents.forEach(ade => columns.push({
		Header: headerForEvent[ade] || ade,
		accessor:ade,
		tooltip: tooltipForEvent[ade]
	}));


	// NB: Tried jcdl package -- it threw dependency related errors (it's not made for using this way I think).
	// TODO total row from ES, not by summing truncated results!
	// no addTotalRow 'cos its misleading

	const limitRows = dataRows.length <= 15 ? null : (
		<div className="form-inline">
			<PropControl label="Rows to display" path={['location', 'params']} prop="rowspp" type="select"
				options={[15, 50, 100, 1000, 10000]}
			/>
		</div>
	);

	return <>
		{incompleteWarning}
		{limitRows}
		<SimpleTable data={dataRows}
			columns={columns}
			headerRender={
				v => typeof(v.accessor) === 'string' && v.accessor !== 'param' ? (
					<LinkMod param="evt" value={v.accessor} text={v.Header || v.accessor} />
				) : v.Header
			}
			rowsPerPage={DataStore.getUrlValue('rowspp') || 15}
			className="table-striped"
			hasFilter
			topRow={totalRow}
			addTotalRow="Page Total"
			showSortButtons
			checkboxValues
			hasCsv
			hideEmpty
			scroller
		/>
	</>;
});


function EventHostTableLine({param, rowName, colNames, data}) {
	assMatch(param, String);
	assMatch(rowName, String);

	let cells = colNames.map(col => {
		if (!data[col] || !data[col][rowName]) return <td key={col} />;
		let v = data[col][rowName];
		let vs = displayValue(v);
		return <td key={col}>{vs}</td>;
	});

	return (
		<tr>
			<td><LinkMod param={param} value={rowName} /></td>
			{cells}
		</tr>
	);
}


/**
 * Memoized to try and speed up dashboard redraws
 * @param {data: key -> Number|Money}
 * @param {param: String} The breakdown-by parameter, e.g. "list by publisher".
 */
const BreakdownWidget = React.memo(({data, param, total, stats}) => {
	assMatch(param, String);
	if (!data) return null;
	// let keys = Object.keys(data).sort( (k1, k2) => data[k1] > data[k2] );
	// let sum = Object.values(data).reduce((s, v) => s+v, 0);
	// let list = keys.map(k => <BreakdownLine key={k} k={k} param={param} value={data[k]} sum={sum} />);
	const type = typeFromParam(param);
	const columns = [
		{
			Header: type || param,
			accessor: 'key',
			Cell: k => {
				if (k === 'Total') return 'Total'; // no link
				let nk = k;
				let item = type && DataStore.getValue('data', type, k);
				if (item) nk = item.name || k;
				return <LinkMod param={param} value={k} text={nk} />;
			}
		},
		{
			Header: 'Amount',
			accessor: 'value',
			Cell: v => <Misc.Money amount={v} minimumFractionDigits={2} maximumFractionDigits={2} />
		}
	];
	const topRow = total ? { key: 'Total', value: total } : null;
	const statsDiv = stats ? <div>
		Count: {stats.count}
		{' '}Min: {printer.prettyNumber(stats.min, 2)}
		{' '}Max: {printer.prettyNumber(stats.max, 2)}
		{' '}Mean: {printer.prettyNumber(stats.avg, 2)}
	</div> : null;

	return (
		<div>
			<SimpleTable columns={columns} dataObject={data} topRow={topRow} hasCsv />
			{statsDiv}
		</div>
	);
});


function FiltersWidget() {
	let modified = isFiltersModified();

	useEffect(() => {
		// Populate the filters on load in the case that a link containing some filters was clicked
		const urlFilters = getUrlFilters();
		if (!Object.values(urlFilters).every(val => noVal(val))) {
			DataStore.setValue(tempFiltersPath, urlFilters);
		}
	}, []); 

	// Click "Load" --> put temp filters in address bar & provoke redraw
	const applyFilters = () => {
		// If we don't shallow-copy, widget.trafficReport.tempFilters and location.params
		// will be the same object - so modifying tempfilters modifies location.params, meaning:
		// (a) report sees change in location.params and reloads on every change to the form fields
		// (b) window url unchanged & out of sync with traffic report , because we're changing
		// location.params without passing through the URL-updating hooks in DataStore.setValue()
		const newFilters = Object.assign({}, DataStore.getValue(tempFiltersPath));
		DataStore.setValue(['location', 'params'], newFilters);
	};

	// const excludeBots = "-invalid:bot -invalid:test ";
	// const excludeBotPath = ["widget", "TrafficReport", "excludeBot"];
	return (
		<div className="well">
			<PropControl prop="q" label="Query" path={tempFiltersPath} />

			<PCSelectBreakdown prop="bby" path={tempFiltersPath} />

			<div>
				<PropControl prop="start" label="Start" path={tempFiltersPath} type="date" />
				<PropControl prop="end" label="End" path={tempFiltersPath} type="date" />
			</div>
			<button type="button" className={'btn '+(modified? 'btn-primary' : 'btn-default')} onClick={applyFilters}>Load Data</button>
		</div>
	);
}


function PCSelectBreakdown({path, prop}) {
	return <PropControl type="select" path={path} prop={prop}
		label="Breakdown by"
		options={['pub', 'via', 'vert', 'campaign', 'variant', 'size', 'os', 'mbl', 'browser', 'env', 'host', 'cid']} dflt="pub"
		labels={{
			pub: "Publisher",
			via: "Agency",
			vert: "Advert",
			campaign: "Ad campaign (which can span several adverts)",
			variant: "AdUnit variant",
			host: "E.g. www.mysite.com. This can be more specific than domain or pub.",
			cid: "Charity"
		}}
	/>;
}

export default TrafficReport;
export {
	BreakdownWidget
};
